import { Button } from "$components/button/button";
import { IconButton } from "$components/button/icon-button";
import { BinIcon } from "$components/icons";
import type { OptionType } from "$components/inputs";
import {
  SelectField as RHFSelectField,
  TextField as RHFTextField,
} from "$components/rhf-fields";
import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps } from "react";
import { useInvoiceCardLayout } from "../invoice-card-shell";
import {
  type InvoiceLineItemDraftRow,
  type InvoiceLineItemRow,
  invoiceLineItemMetaLine,
} from "./invoice-line-item-row";
import type { InvoiceItemOption } from "./invoice-view";
import { PriceListItemField } from "./price-list-item-field";
import {
  INVOICE_CARD_ACCENT_BAR_WIDTH,
  INVOICE_CARD_COLORS,
  type InvoicePayerKind,
  invoiceAccentColor,
  invoiceAmountColor,
  invoiceRowBackgroundColor,
} from "./tokens";

const DISCLOSURE_COLUMN_WIDTH = 14;

const lineItemGridStyles = ({ hasDisclosure }: { hasDisclosure: boolean }) =>
  ({
    display: "grid",
    gridTemplateColumns: hasDisclosure
      ? `minmax(0, 1fr) auto ${DISCLOSURE_COLUMN_WIDTH}px`
      : "1fr auto",
    gap: hasDisclosure ? "3px 10px" : "3px 12px",
    padding: hasDisclosure ? "11px 12px 12px 13px" : "11px 16px 12px 13px",
  }) as const;

const CardRoot = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind; "data-grid": boolean }
>(({ theme, ...props }) => ({
  borderBottom: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  borderLeft: `${INVOICE_CARD_ACCENT_BAR_WIDTH}px solid ${invoiceAccentColor(
    theme,
    props["data-payer-kind"],
  )}`,
  backgroundColor: invoiceRowBackgroundColor(theme, props["data-payer-kind"]),
  ...(props["data-grid"] ? lineItemGridStyles({ hasDisclosure: false }) : {}),
}));

const DisclosureButton = styled("button")({
  ...lineItemGridStyles({ hasDisclosure: true }),
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
  border: 0,
  background: "transparent",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
});

const ItemName = styled("div")<Theme>(({ theme }) => ({
  gridColumn: 1,
  gridRow: 1,
  minWidth: 0,
  fontWeight: 600,
  fontSize: 14,
  color: theme.palette.text.primary,
  textWrap: "pretty",
}));

const Amount = styled("div")<Theme, { "data-payer-kind": InvoicePayerKind }>(
  ({ theme, ...props }) => ({
    gridColumn: 2,
    gridRow: 1,
    fontWeight: 700,
    fontSize: 15,
    whiteSpace: "nowrap",
    textAlign: "right",
    color: invoiceAmountColor(theme, props["data-payer-kind"]),
  }),
);

const MetaLine = styled("div")<Theme>(({ theme }) => ({
  gridColumn: 1,
  gridRow: 2,
  minWidth: 0,
  fontWeight: 500,
  fontSize: 12.5,
  color: theme.palette.text.secondary,
}));

const PayerLabel = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind }
>(({ theme, ...props }) => ({
  gridColumn: 2,
  gridRow: 2,
  fontWeight: 600,
  fontSize: 12.5,
  whiteSpace: "nowrap",
  textAlign: "right",
  color: invoiceAccentColor(theme, props["data-payer-kind"]),
}));

const Chevron = styled("div")<Theme>(({ theme }) => ({
  gridColumn: 3,
  gridRow: "1 / span 2",
  fontWeight: 500,
  fontSize: 12,
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const Editor = styled("div")<Theme, { "data-icon-padding": number }>(
  ({ ...props }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "4px 13px 14px",
    // Selects wrap rather than ellipsize — a tariff label must never truncate.
    "& .MuiSelect-select": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      lineHeight: 1.35,
    },
    "& .MuiIconButton-root": { padding: props["data-icon-padding"] },
    "& .MuiSvgIcon-root": { fontSize: 18 },
    // The autocomplete's dropdown arrow is an IconButton too, and the touch
    // padding above would push the field past its 45–48px height.
    "& .MuiAutocomplete-popupIndicator": { padding: 2 },
  }),
);

const EditorRow = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

const FieldSlot = styled("div")<Theme, { "data-flex": string }>(
  ({ ...props }) => ({
    flex: props["data-flex"],
    minWidth: 0,
  }),
);

const EditorActions = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
});

/**
 * The draft makes the whole card a disclosure button over a full-size editor,
 * because a dense cell is not a touch target.
 */
export const InvoiceLineItemCard: FC<
  {
    showItemDates?: boolean;
  } & (
    | {
        variant: "draft";
        row: InvoiceLineItemDraftRow;
        itemOptions: InvoiceItemOption[];
        departmentOptions: OptionType[];
        supplierOptions: OptionType[];
        isExpanded: boolean;
        onToggleExpanded: () => void;
        onRemove: () => void;
        removeItemLabel?: string;
        doneLabel?: string;
      }
    | {
        variant: "finalised";
        row: InvoiceLineItemRow;
        itemOptions?: never;
        departmentOptions?: never;
        supplierOptions?: never;
        isExpanded?: never;
        onToggleExpanded?: never;
        onRemove?: never;
        removeItemLabel?: never;
        doneLabel?: never;
      }
  ) &
    Omit<
      ComponentProps<typeof CardRoot>,
      "data-payer-kind" | "data-grid" | "children"
    >
> = ({
  variant,
  row,
  showItemDates = true,
  itemOptions,
  departmentOptions,
  supplierOptions,
  isExpanded = false,
  onToggleExpanded,
  onRemove,
  removeItemLabel = "Remove item",
  doneLabel = "Done",
  ...restProps
}) => {
  const { iconButtonPadding } = useInvoiceCardLayout();

  const metaLine = invoiceLineItemMetaLine({
    row,
    variant,
    showItemDate: showItemDates,
  });

  const summary = (
    <>
      <ItemName>{row.name}</ItemName>
      <Amount data-payer-kind={row.payerKind}>{row.amountLabel}</Amount>
      <MetaLine>{metaLine}</MetaLine>
      <PayerLabel data-payer-kind={row.payerKind}>{row.payerLabel}</PayerLabel>
    </>
  );

  if (variant === "finalised") {
    return (
      <CardRoot data-payer-kind={row.payerKind} data-grid {...restProps}>
        {summary}
      </CardRoot>
    );
  }

  return (
    <CardRoot data-payer-kind={row.payerKind} data-grid={false} {...restProps}>
      <DisclosureButton
        type="button"
        aria-expanded={isExpanded}
        onClick={onToggleExpanded}
      >
        {summary}
        <Chevron aria-hidden>{isExpanded ? "▴" : "▾"}</Chevron>
      </DisclosureButton>
      {isExpanded && (
        <Editor data-icon-padding={iconButtonPadding}>
          <PriceListItemField
            name={row.fieldNames.name}
            label="Item"
            options={itemOptions}
          />
          <EditorRow>
            <FieldSlot data-flex="1 1 150px">
              <RHFSelectField
                name={row.fieldNames.department}
                label="Revenue dpt"
                options={departmentOptions}
                fullWidth
                hideHelper
              />
            </FieldSlot>
            <FieldSlot data-flex="1 1 130px">
              <RHFSelectField
                name={row.fieldNames.supplier}
                label="Supplier"
                options={supplierOptions}
                fullWidth
                hideHelper
              />
            </FieldSlot>
          </EditorRow>
          <EditorRow>
            <FieldSlot data-flex="0 0 76px">
              <RHFTextField
                name={row.fieldNames.quantity}
                label="Qty"
                fullWidth
                hideHelper
              />
            </FieldSlot>
            <FieldSlot data-flex="1 1 200px">
              <RHFSelectField
                name={row.fieldNames.payer}
                label="Payer & tariff"
                options={row.payerOptions}
                fullWidth
                hideHelper
              />
            </FieldSlot>
          </EditorRow>
          <EditorActions>
            <IconButton
              color="error"
              size="small"
              aria-label={removeItemLabel}
              onClick={onRemove}
            >
              <BinIcon fill="currentColor" />
            </IconButton>
            <Button color="secondary" size="small" onClick={onToggleExpanded}>
              {doneLabel}
            </Button>
          </EditorActions>
        </Editor>
      )}
    </CardRoot>
  );
};
